import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "../../data/site";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#estimate" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            className="font-serif-display text-2xl md:text-[28px] tracking-tight text-[#F5F5F0] leading-none"
          >
            Cresta <span className="text-champagne italic">Bath & Tile</span>
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="text-[13px] tracking-wider text-[#A3A3A3] hover:text-[#F5F5F0] underline-grow font-sans-body"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-phone-link"
              className="hidden md:inline-flex items-center gap-2 text-[13px] tracking-wider text-champagne hover:text-[#D4AF37] font-sans-body"
            >
              <Phone className="w-3.5 h-3.5" />
              {CONTACT.phone}
            </a>
            <a
              href="#estimate"
              data-testid="nav-estimate-btn"
              className="hidden md:inline-block bg-champagne text-[#0A0A0A] text-[12px] tracking-[0.18em] uppercase font-semibold px-5 py-3 rounded-sm hover:bg-[#D4AF37] transition-colors"
            >
              Free Estimate
            </a>
            <button
              data-testid="mobile-menu-trigger"
              onClick={() => setOpen(true)}
              className="lg:hidden text-[#F5F5F0] p-2"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile sheet */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-[60] bg-[#0A0A0A] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-white/5">
          <span className="font-serif-display text-2xl">
            Cresta <span className="text-champagne italic">Bath & Tile</span>
          </span>
          <button
            data-testid="mobile-menu-close"
            onClick={() => setOpen(false)}
            className="text-[#F5F5F0] p-2"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex flex-col px-6 py-10 gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="font-serif-display text-4xl text-[#F5F5F0]"
            >
              {n.label}
            </a>
          ))}
          <div className="mt-6 pt-8 border-t border-white/5 space-y-3">
            <a href={CONTACT.phoneHref} className="block text-champagne text-lg">{CONTACT.phone}</a>
            <a href={CONTACT.emailHref} className="block text-[#A3A3A3]">{CONTACT.email}</a>
          </div>
          <a
            href="#estimate"
            onClick={() => setOpen(false)}
            className="mt-4 inline-block text-center bg-champagne text-[#0A0A0A] text-sm tracking-[0.18em] uppercase font-semibold px-5 py-4 rounded-sm"
          >
            Get a Free Estimate
          </a>
        </nav>
      </div>

      {/* Mobile sticky bottom bar */}
      <a
        href="#estimate"
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-4 left-4 right-4 z-40 bg-champagne text-[#0A0A0A] text-center text-sm tracking-[0.2em] uppercase font-semibold py-4 rounded-sm shadow-2xl"
      >
        Get a Free Estimate
      </a>
    </>
  );
}
